import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Permission hook (JWT-only).
 *
 * Reads scope strings (e.g. 'events.read') from the decoded JWT. No API calls.
 * Source of truth is the JWT issued at login or refresh.
 */

export const useModulePermissions = () => {
  const { decodedToken } = useAuth();

  const jwtScopes = useMemo(() => decodedToken?.scopes ?? [], [decodedToken]);

  const isSystemAdmin = useMemo(
    () => decodedToken?.isSystemAdmin === true,
    [decodedToken]
  );

  // Identified by the isSchoolAdmin claim baked into the JWT at login.
  // The role can be renamed in the UI without breaking authorization.
  const isSchoolAdmin = useMemo(
    () => decodedToken?.isSchoolAdmin === true,
    [decodedToken]
  );

  // Identified by the isTeacher claim baked into the JWT at login (set when
  // the school's system-generated Teacher role is created). The role can be
  // renamed in the UI without breaking the restriction. Custom roles with
  // attendance/marks scopes are NOT Teachers — only the deliberately-flagged
  // role is restricted.
  const isTeacher = useMemo(
    () => decodedToken?.isTeacher === true,
    [decodedToken]
  );

  const canRead = useCallback((moduleCode: string): boolean => {
    if (isSystemAdmin || isSchoolAdmin) return true;
    return jwtScopes.includes('all') || jwtScopes.includes(`${moduleCode}.read`);
  }, [isSystemAdmin, isSchoolAdmin, jwtScopes]);

  const canWrite = useCallback((moduleCode: string): boolean => {
    if (isSystemAdmin || isSchoolAdmin) return true;
    return jwtScopes.includes('all') || jwtScopes.includes(`${moduleCode}.write`);
  }, [isSystemAdmin, isSchoolAdmin, jwtScopes]);

  const readableModules = useMemo(() => {
    if (isSystemAdmin || isSchoolAdmin || jwtScopes.includes('all')) return ['all'];
    return jwtScopes
      .filter(scope => scope.endsWith('.read'))
      .map(scope => scope.replace('.read', ''));
  }, [isSystemAdmin, isSchoolAdmin, jwtScopes]);

  const writableModules = useMemo(() => {
    if (isSystemAdmin || isSchoolAdmin || jwtScopes.includes('all')) return ['all'];
    return jwtScopes
      .filter(scope => scope.endsWith('.write'))
      .map(scope => scope.replace('.write', ''));
  }, [isSystemAdmin, isSchoolAdmin, jwtScopes]);

  return {
    canRead,
    canWrite,
    readableModules,
    writableModules,
    isAdmin: isSystemAdmin || isSchoolAdmin,
    isSystemAdmin,
    isSchoolAdmin,
    isTeacher,
    scopes: jwtScopes,
  };
};

